import React, { useEffect, useRef } from "react";
import { DockviewApi } from "dockview-react";
import { MdEdit } from "react-icons/md";
import { IoTrashBinSharp } from "react-icons/io5";
import { api } from "./utils/api";
import { isValidDateString } from "./utils/utils";
import { daysRemaining } from "./Calendar";
import { TickerLink } from "./TickerActionMenu";

const SPLIT_STORAGE_KEY = "calendarPanelTopHeightPercent";

function loadInitialTopHeightPercent(): number {
  try {
    const raw = localStorage.getItem(SPLIT_STORAGE_KEY);
    const parsed = raw ? parseFloat(raw) : NaN;
    return isNaN(parsed) ? 60 : Math.min(85, Math.max(15, parsed));
  } catch {
    return 60;
  }
}

interface ITickerPrice {
  last_price: number;
  change_abs: number;
  change_rel: number;
}

interface IUpcomingEvent {
  id: number;
  date: Date;
  title: string;
  description: string;
  tickers: string[];
  ticker_prices?: Record<string, ITickerPrice>;
}

interface UpcomingCalendarPanelProps {
  containerApi?: DockviewApi;
}

interface IEditingUpcomingEvent {
  id: number;
  title: string;
  tickers: string;
  date: string;
  description: string;
}

// Change indicator is jet black regardless of direction (no up/down color coding here).
const CHART_UP_COLOR = "#0a0a0a";
const CHART_DOWN_COLOR = "#0a0a0a";

// The "Upcoming" calendar list, previously embedded in the Main dockview panel - now a
// standalone always-visible sidebar rendered outside of dockview (see App.tsx).
export const UpcomingCalendarPanel = ({ containerApi }: UpcomingCalendarPanelProps) => {
  const [upcomingEvents, setUpcomingEvents] = React.useState<IUpcomingEvent[]>(
    [],
  );

  // Vertical split between the calendar (top) and the unique-tickers strip (bottom), draggable
  const [topHeightPercent, setTopHeightPercent] = React.useState(loadInitialTopHeightPercent);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    fetchUpcomingEvents();
    // events can be added/removed from the Calendar dockview panel at any time, so keep this
    // always-visible sidebar in sync rather than requiring a reload
    const interval = setInterval(fetchUpcomingEvents, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!draggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((e.clientY - rect.top) / rect.height) * 100;
      setTopHeightPercent(Math.min(85, Math.max(15, pct)));
    }
    function onMouseUp() {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setTopHeightPercent((current) => {
        try {
          localStorage.setItem(SPLIT_STORAGE_KEY, String(current));
        } catch {
          // ignore
        }
        return current;
      });
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  function startDrag(e: React.MouseEvent) {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
  }

  function fetchUpcomingEvents() {
    api
      .get("/tracked-events")
      .then((response) => {
        setUpcomingEvents(
          response.data.map((event: IUpcomingEvent) => ({
            ...event,
            date: new Date(event.date),
          })),
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  // Compact inline edit form, same fields/endpoint as the Calendar component's edit form
  // (title/tickers/date via POST /calendar-events), minus the rich-text description editor
  // to keep this narrow always-visible sidebar small.
  const [editingEvent, setEditingEvent] = React.useState<IEditingUpcomingEvent | null>(null);
  const [editError, setEditError] = React.useState("");

  function startEditEvent(event: IUpcomingEvent) {
    setEditError("");
    setEditingEvent({
      id: event.id,
      title: event.title,
      tickers: event.tickers.join(" "),
      date: event.date.toISOString().split("T")[0],
      description: event.description,
    });
  }

  function submitEditEvent() {
    if (!editingEvent) return;

    if (editingEvent.title.trim() === "") {
      setEditError("Title is required");
      return;
    }

    if (!isValidDateString(editingEvent.date)) {
      setEditError("Date must be in YYYY-MM-DD format");
      return;
    }

    const tickerPattern = /^[a-zA-Z0-9 ]*$/;
    if (!tickerPattern.test(editingEvent.tickers)) {
      setEditError("Tickers can only contain alphanumeric characters and spaces");
      return;
    }

    setEditError("");

    api
      .post("/calendar-events", {
        id: editingEvent.id,
        title: editingEvent.title,
        tickers: editingEvent.tickers,
        date: editingEvent.date,
        description: editingEvent.description,
      })
      .then(() => {
        setEditingEvent(null);
        fetchUpcomingEvents();
      })
      .catch((error) => {
        console.error(error);
        setEditError("Error submitting event");
      });
  }

  function deleteUpcomingEvent(eventId: number) {
    api
      .delete(`/calendar-events/${eventId}`)
      .then(() => {
        setUpcomingEvents((prev) => prev.filter((e) => e.id !== eventId));
        setEditingEvent((prev) => (prev?.id === eventId ? null : prev));
      })
      .catch((error) => {
        console.error(error);
      });
  }

  function renderTicker(ticker: string, price: ITickerPrice | undefined) {
    return (
      <div key={ticker} className="flex items-baseline gap-4">
        <TickerLink ticker={ticker} containerApi={containerApi} className="font-bold cursor-pointer" />
        {price && (
          <>
            <span className="text-gray-500 tabular-nums font-semibold">
              {price.last_price.toFixed(2)}
            </span>
            <span
              className="text-[0.95em] font-semibold tabular-nums"
              style={{ color: price.change_abs >= 0 ? CHART_UP_COLOR : CHART_DOWN_COLOR }}
            >
              {price.change_abs >= 0 ? "+" : ""}
              {price.change_abs.toFixed(1)}
            </span>
          </>
        )}
      </div>
    );
  }

  // Same tickers as in the events above, in the same order of first appearance, but
  // deduplicated and without the surrounding event/date context.
  const uniqueTickers: { ticker: string; price?: ITickerPrice }[] = [];
  const seenTickers = new Set<string>();
  for (const event of upcomingEvents) {
    for (const ticker of event.tickers) {
      if (seenTickers.has(ticker)) continue;
      seenTickers.add(ticker);
      uniqueTickers.push({ ticker, price: event.ticker_prices?.[ticker] });
    }
  }

  return (
    <div ref={splitContainerRef} className="flex h-full flex-col overflow-hidden">
      <div
        className="overflow-auto p-4"
        style={{ height: `${topHeightPercent}%` }}
      >
      <h1>Upcoming</h1>
      {upcomingEvents.map((event) => (
        <div key={event.id} className="p-2 gap-1">
          <div className="flex items-center justify-between gap-2">
            <p className="font-sans">{event.title}</p>
            <div className="flex items-center gap-1 shrink-0">
              <MdEdit
                size={13}
                className="cursor-pointer text-gray-500 hover:text-gray-800"
                onClick={() => startEditEvent(event)}
              />
              <IoTrashBinSharp
                size={13}
                className="cursor-pointer text-gray-500 hover:text-red-600"
                onClick={() => deleteUpcomingEvent(event.id)}
              />
            </div>
          </div>

          <p className="text-sm text-gray-600">
            {event.date.toDateString()}{" "}
            <span>({daysRemaining(event.date)}d)</span>
          </p>

          {event.tickers.map((ticker) => renderTicker(ticker, event.ticker_prices?.[ticker]))}

          {editingEvent && editingEvent.id === event.id && (
            <div className="mt-1 space-y-1">
              <input
                className="w-full text-xs"
                placeholder="Title"
                type="text"
                value={editingEvent.title}
                onChange={(e) =>
                  setEditingEvent((prev) => prev && { ...prev, title: e.target.value })
                }
              />
              <div className="flex gap-1 items-center">
                <input
                  className="flex-1 min-w-0 text-xs"
                  placeholder="AAPL GOOG"
                  type="text"
                  value={editingEvent.tickers}
                  onChange={(e) =>
                    setEditingEvent((prev) => prev && { ...prev, tickers: e.target.value })
                  }
                />
                <input
                  className="flex-1 min-w-0 text-xs"
                  type="text"
                  placeholder="YYYY-MM-DD"
                  value={editingEvent.date}
                  onChange={(e) =>
                    setEditingEvent((prev) => prev && { ...prev, date: e.target.value })
                  }
                />
              </div>
              <div className="flex gap-2 items-center">
                <button
                  type="button"
                  className="btn btn-default text-xs px-2 py-0.5"
                  onClick={submitEditEvent}
                >
                  Save
                </button>
                <a className="text-xs text-gray-500 cursor-pointer" onClick={() => setEditingEvent(null)}>
                  Cancel
                </a>
              </div>
              {editError && <p className="text-xs text-red-500">{editError}</p>}
            </div>
          )}
        </div>
      ))}
      </div>

      <div
        onMouseDown={startDrag}
        className="h-1.5 shrink-0 cursor-row-resize bg-gray-200 hover:bg-blue-300 active:bg-blue-400 dark:bg-neutral-700"
        title="Drag to resize"
      />

      <div
        className="overflow-auto p-4"
        style={{ height: `${100 - topHeightPercent}%` }}
      >
        {uniqueTickers.map(({ ticker, price }) => renderTicker(ticker, price))}
      </div>

    </div>
  );
};
